// assets/components/searchPredictive.js
export function initSearchPredictive() {
	const forms = document.querySelectorAll(".js-search-form");
	if (!forms.length) return;
	forms.forEach((form, idx) => setupPredictiveInstance(form, idx));
}

function setupPredictiveInstance(form, index) {
	const input = form.querySelector(".js-search-input");
	const dropdown = form.querySelector(".js-search-dropdown");
	const listbox = form.querySelector(".js-search-results"); // <ul role="listbox">
	const loadingEl = form.querySelector(".js-search-loading");
	const emptyEl = form.querySelector(".js-search-empty");
	const footerEl = form.querySelector(".js-search-footer");
	const combobox = form.querySelector('[role="combobox"]');

	if (!input || !dropdown || !listbox || !combobox) return;

	// ---- Rendi univoci gli ID/ARIA per questa istanza ----
	const uid = `ps-${Date.now().toString(36)}-${index}-${Math.random()
		.toString(36)
		.slice(2, 6)}`;
	const listboxId = `predictive-search-listbox-${uid}`;
	const dropdownId = `predictive-search-dropdown-${uid}`;

	listbox.id = listboxId; // sovrascrive eventuali ID duplicati
	dropdown.id = dropdownId;
	combobox.setAttribute("aria-owns", listboxId);
	input.setAttribute("aria-controls", listboxId);

	// ---- Config ----
	const predictiveUrl =
		form.getAttribute("data-predictive-url") || "/search/suggest.json";
	const searchUrl = form.getAttribute("data-search-url") || "/search";
	const locale = form.getAttribute("data-locale") || "it-IT";
	const currency = form.getAttribute("data-currency") || "EUR";

	const MIN_CHARS = 3;
	const LIMIT = 8;
	const DEBOUNCE_MS = 200;

	let activeIndex = -1;
	let currentResults = [];
	let controller = null;

	// ---- Utils ----
	const formatMoney = (amount) => {
		if (typeof amount !== "number") return "";
		try {
			return new Intl.NumberFormat(locale, {
				style: "currency",
				currency,
			}).format(amount / 100);
		} catch {
			return `€ ${(amount / 100).toFixed(2)}`;
		}
	};

	const debounce = (fn, ms = 200) => {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn(...args), ms);
		};
	};

	const openDropdown = () => {
		dropdown.classList.remove("hidden");
		combobox.setAttribute("aria-expanded", "true");
	};

	const closeDropdown = () => {
		dropdown.classList.add("hidden");
		combobox.setAttribute("aria-expanded", "false");
		activeIndex = -1;
		input.setAttribute("aria-activedescendant", "");
	};

	const setLoading = (on) =>
		loadingEl && loadingEl.classList.toggle("hidden", !on);
	const setEmpty = (on) => emptyEl && emptyEl.classList.toggle("hidden", !on);

	const setFooterLink = (term) => {
		if (!footerEl) return;
		if (!term || term.length < MIN_CHARS) {
			footerEl.classList.add("hidden");
			footerEl.innerHTML = "";
			return;
		}
		const url = `${searchUrl}?q=${encodeURIComponent(term)}`;
		footerEl.innerHTML = `<a class="underline hover:no-underline" href="${url}">Vedi tutti i risultati</a>`;
		footerEl.classList.remove("hidden");
	};

	const setActive = (idx) => {
		const items = listbox.querySelectorAll('[role="option"]');
		items.forEach((el) => el.classList.remove("bg-secondary/10"));
		activeIndex = idx;
		if (idx >= 0 && items[idx]) {
			items[idx].classList.add("bg-secondary/10");
			input.setAttribute("aria-activedescendant", items[idx].id);
		} else {
			input.setAttribute("aria-activedescendant", "");
		}
	};

	const renderResults = (products = []) => {
		listbox.innerHTML = "";
		currentResults = products.slice(0, LIMIT);
		activeIndex = -1;

		if (!currentResults.length) {
			setEmpty(true);
			openDropdown();
			return;
		}
		setEmpty(false);

		const frag = document.createDocumentFragment();
		currentResults.forEach((p, i) => {
			const li = document.createElement("li");
			const optId = `predictive-option-${uid}-${i}`;
			li.id = optId;
			li.setAttribute("role", "option");
			li.className =
				"px-4 py-3 hover:bg-secondary/50 cursor-pointer flex items-center gap-3";
			li.dataset.index = String(i);

			const imgUrl =
				p?.featured_image?.url ||
				p?.image?.url ||
				(Array.isArray(p?.images) && p.images[0]?.url) ||
				null;

			const price =
				typeof p?.price === "number"
					? p.price
					: typeof p?.price_min === "number"
					? p.price_min
					: null;
			const priceHtml =
				price != null
					? `<span class="block text-xs opacity-70">${formatMoney(
							price
					  )}</span>`
					: "";

			li.innerHTML = `
        ${
			imgUrl
				? `<img src="${imgUrl}" alt="" class="w-10 h-10 object-cover rounded-md shrink-0">`
				: ""
		}
        <div class="min-w-0">
          <a href="${
				p?.url || p?.url_to || "#"
			}" class="block truncate text-sm leading-snug">${
				p?.title || "Prodotto"
			}</a>
          ${priceHtml}
        </div>
      `;

			li.addEventListener("mouseenter", () => setActive(i));
			li.addEventListener("mouseleave", () => setActive(-1));
			li.addEventListener("click", () => {
				const href = p?.url || p?.url_to;
				if (href) window.location.href = href;
			});

			frag.appendChild(li);
		});

		listbox.appendChild(frag);
		openDropdown();
	};

	const fetchResults = async (term) => {
		if (controller) controller.abort();
		controller = new AbortController();

		const url = new URL(predictiveUrl, window.location.origin);
		url.searchParams.set("q", term);
		url.searchParams.set("resources[type]", "product");
		url.searchParams.set("resources[limit]", String(LIMIT));
		url.searchParams.set(
			"resources[options][unavailable_products]",
			"last"
		);
		url.searchParams.set(
			"resources[options][fields]",
			"title,variants.title,product_type,vendor"
		);

		setLoading(true);
		try {
			const res = await fetch(url.toString(), {
				signal: controller.signal,
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Bad response");
			const data = await res.json();
			const products =
				data?.resources?.results?.products ||
				data?.products ||
				data?.resources?.products ||
				[];
			renderResults(products);
		} catch (err) {
			if (err.name !== "AbortError") {
				console.warn("Predictive search error:", err);
				renderResults([]);
			}
		} finally {
			setLoading(false);
		}
	};

	// ---- Eventi per questa istanza ----
	const handleInput = debounce((e) => {
		const term = (e.target.value || "").trim();
		setFooterLink(term);
		if (term.length < MIN_CHARS) {
			closeDropdown();
			return;
		}
		fetchResults(term);
	}, DEBOUNCE_MS);

	input.addEventListener("input", handleInput);

	input.addEventListener("focus", () => {
		const term = (input.value || "").trim();
		if (term.length >= MIN_CHARS && (currentResults?.length || 0) > 0) {
			openDropdown();
		}
	});

	input.addEventListener("keydown", (e) => {
		if (dropdown.classList.contains("hidden")) return;
		const items = listbox.querySelectorAll('[role="option"]');
		if (["ArrowDown", "ArrowUp"].includes(e.key)) {
			e.preventDefault();
			if (!items.length) return;
			if (e.key === "ArrowDown")
				setActive((activeIndex + 1) % items.length);
			else setActive((activeIndex - 1 + items.length) % items.length);
			return;
		}
		if (e.key === "Enter") {
			if (activeIndex >= 0 && items[activeIndex]) {
				e.preventDefault();
				const link = items[activeIndex].querySelector("a[href]");
				if (link) window.location.href = link.getAttribute("href");
			}
			return;
		}
		if (e.key === "Escape") {
			e.preventDefault();
			closeDropdown();
		}
	});

	document.addEventListener("click", (e) => {
		if (!form.contains(e.target)) closeDropdown();
	});

	form.addEventListener("submit", () => closeDropdown());
}
